import { CloseIcon } from "@chakra-ui/icons";
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { useCallback, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useCreateOrEditVideo from "../../hooks/api/video/useCreateOrEditVideo";
import { Video } from "../../models/Video";
import useAuthState from "../../state/auth.state";
import { getVideoUrl } from "../../utils/common";
import VideoPlayer from "../VideoPlayer";

type FormValues = Omit<Video, "status">;

type Props = {
  video?: Video;
  onSuccess?: (videoId: number) => void;
  onError?: () => void;
};

export default function VideoForm({ video, onSuccess, onError }: Props) {
  const { t } = useTranslation();
  const { role } = useAuthState();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    handleSubmit,
    register,
    watch,
    control,
    getValues,
    setValue,
    formState: { errors, isValid, isSubmitted },
  } = useForm<FormValues>({
    defaultValues: {
      title: video?.title || "",
      description: video?.description || "",
      url: video ? getVideoUrl(video) : undefined,
      isExternal: !!video?.url,
    },
  });

  const { mutateAsync: createOrEditVideo, isPending } = useCreateOrEditVideo();

  const onSubmit = useCallback(
    (values: FormValues) => {
      createOrEditVideo({
        video: {
          id: video?.id,
          status: role === "ROLE_TEACHER" ? "PENDING" : "APPROVED",
          ...values,
        },
        operation: video ? "edit" : "create",
      })
        .then((response) => onSuccess?.(response.id))
        .catch(onError);
    },
    [createOrEditVideo, onError, onSuccess, role, video],
  );

  const clearFileInput = useCallback(() => {
    setValue("file", undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormControl
        isDisabled={video?.status === "DISABLED"}
        isInvalid={!isValid && isSubmitted}
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <div>
          <FormLabel htmlFor="title">{t("forms.title")}</FormLabel>
          <Input
            id="title"
            placeholder={t("forms.title")}
            {...register("title", { required: t("forms.validation.required") })}
          />
          <FormErrorMessage>
            {errors.title && errors.title.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="description">{t("forms.description")}</FormLabel>
          <Input
            id="description"
            placeholder={t("forms.description")}
            {...register("description", {
              required: t("forms.validation.required"),
            })}
          />
          <FormErrorMessage>
            {errors.description && errors.description.message}
          </FormErrorMessage>
        </div>
        <div>
          <FormLabel htmlFor="url">{t("forms.url")}</FormLabel>
          <Input
            id="url"
            placeholder={t("forms.url")}
            {...register("url", {
              validate: (value: string) =>
                !value && !getValues("file")
                  ? t("forms.validation.required_if", {
                      param1: t("forms.url").toLowerCase(),
                      param2: t("forms.file").toLowerCase(),
                    })
                  : undefined,
            })}
          />
          <FormErrorMessage>
            {errors.url && errors.url.message}
          </FormErrorMessage>
        </div>
        {watch("url") && <VideoPlayer url={watch("url")} />}
        <div>
          <FormLabel htmlFor="file">{t("forms.file")}</FormLabel>
          <Controller
            name="file"
            control={control}
            render={({ field: { value, onChange, ...field } }) => (
              <InputGroup>
                <Input
                  id="file"
                  type="file"
                  accept="video/mp4, video/mov"
                  {...field}
                  ref={fileInputRef}
                  value={value?.fileName}
                  onChange={(event) => onChange(event.target.files?.[0])}
                  placeholder={t("forms.file")}
                />
                <InputRightElement cursor="pointer">
                  <CloseIcon color="gray.600" onClick={clearFileInput} />
                </InputRightElement>
              </InputGroup>
            )}
          />
        </div>
      </FormControl>
      <Button
        mt={4}
        bg="primary.DEFAULT"
        color="white"
        isLoading={isPending}
        type="submit"
        isDisabled={video?.status === "DISABLED"}
      >
        {t("common.buttons.save")}
      </Button>
    </form>
  );
}
